/**
 * W5 OWNERIN fix 1 (review B1; CLAUDE.md section 3 T3, section 0.5, B13): the
 * T3-substance screen. It reads an owner's note for what applying it would
 * change, and holds the note when that is something only Kabir changes in code:
 *
 *  - a CONCESSION (a lower number, a match, a throw-in, an offer, anything
 *    free): price and discount caps, and the no-offer rule of section 0.5;
 *  - a CUSTOMER SEGMENT (VIP, big spender, repeat buyer, whale): VIP status
 *    changes routing and service level, never a number (section 3);
 *  - the LAB GROWN disclosure (FTC Jewelry Guides, compliance rules), by any
 *    mention, and a verb that skips or hides a disclosure;
 *  - a REPLY TIME (a time figure, or "faster", beside a reply or a response):
 *    SLA timers;
 *  - any wording that sets a CAP, a timer, a trigger, an escalation or a
 *    compliance line.
 *
 * It runs BEFORE the class read and BEFORE any model call, on every path
 * (`processOwnerNote`, so `pnpm teach pool` and the weekly step alike), and
 * again where a note leaves the pool (`loadIdeaPool`) and where a predicate is
 * mapped (`mapNoteByKeywords`). It is deliberately wider than `readOwnerNoteTier`
 * and works on the note's own words: precision beats recall, and a held note
 * costs the owner one line, where a laundered one spends client trust. The
 * lists are examples of intent, not a boundary the owner can phrase around:
 * when unsure, hold. AWAITING RATIFICATION (Kabir has not ruled these lists;
 * they come from adversarial review 1 of W5).
 *
 * Pure: the same text always reads the same, nothing is thrown, no model.
 */

/** A whole word, not part of a longer one, in any script. */
const W = (alternatives: string): RegExp =>
  new RegExp(`(?<![\\p{L}\\p{N}])(?:${alternatives})(?![\\p{L}\\p{N}])`, "iu");

const CONCESSION = W(
  [
    "lower(?:s|ed|ing)?",
    "knock(?:s|ed|ing)? (?:\\w+ ){0,3}(?:off|down)",
    "generous(?:ly)?|generosity",
    "match(?:es|ed|ing)?",
    "throw(?:s|ing)? in|threw in",
    "deals?",
    "offer(?:s|ed|ing)?",
    "sales?",
    "specials?",
    "on the house",
    "complimentary",
    "discount(?:s|ed|ing)?",
    "percent off|per cent off|\\d+(?:\\.\\d+)?\\s?% off",
    "free(?:bies|bie)?",
    "cheap(?:er|est|ly)?",
    "reduc(?:e|es|ed|ing|tion|tions)",
    "waiv(?:e|es|ed|ing)",
    "bargain(?:s|ed|ing)?",
    "haggl(?:e|es|ed|ing)",
    "negotiat(?:e|es|ed|ing|ion|ions)",
    "concessions?",
    "incentiv(?:e|es|ised|ized)",
    "perks?",
    "bonus(?:es)?",
    "give(?:s|n)? away|giving away",
    // fix 2 (re-review probe misses)
    "halfway",
    "sweeten(?:s|ed|ing)?",
    "comp(?:s|ed|ing)?",
    "(?:eat|eats|eating|ate) the",
    "on us",
    "cover(?:s|ed|ing)? the",
  ].join("|"),
);

const SEGMENT = W(
  [
    "v\\.?i\\.?ps?",
    "big[\\s-]?spenders?|top[\\s-]?spenders?|high[\\s-]?rollers?",
    "repeat (?:buyers?|customers?|clients?|shoppers?|purchasers?)",
    "returning (?:buyers?|customers?|clients?|shoppers?)",
    "loyal (?:buyers?|customers?|clients?|shoppers?)",
    "whales?",
    "high[\\s-]?(?:value|net[\\s-]?worth)",
    "best (?:customers?|clients?|buyers?)",
    // fix 2 (re-review probe misses)
    "white[\\s-]?glove",
    "best people|our best",
    "top[\\s-]?tier",
  ].join("|"),
);

const LAB_GROWN = W("lab[\\s-]?(?:grown|created|made)|lab[\\s-]?diamonds?|man[\\s-]?made|synthetic|cultured(?: diamonds?)?|(?:created|grown) (?:diamonds?|stones?|gems?)|cvd|hpht");

const HIDE_VERB = "skip(?:s|ped|ping)?|omit(?:s|ted|ting)?|leav(?:e|es|ing) out|left out|not mention(?:ing|ed)?|never mention(?:ing|ed)?|no mention|don'?t mention|without mention(?:ing)?|hid(?:e|es|ing|den)|conceal(?:s|ed|ing)?|downplay(?:s|ed|ing)?|avoid(?:s|ed|ing)?|drop(?:s|ped|ping)?";
const DISCLOSURE_WORD = "disclos(?:e|es|ed|ing|ure|ures)|origins?|provenance|certif(?:y|ied|icate|icates|ication)";
const HIDE_NEAR_DISCLOSURE = new RegExp(
  `(?<![\\p{L}\\p{N}])(?:${HIDE_VERB})(?![\\p{L}\\p{N}])[^.!?\\n]{0,60}(?<![\\p{L}\\p{N}])(?:${DISCLOSURE_WORD})(?![\\p{L}\\p{N}])|(?<![\\p{L}\\p{N}])(?:${DISCLOSURE_WORD})(?![\\p{L}\\p{N}])[^.!?\\n]{0,60}(?<![\\p{L}\\p{N}])(?:${HIDE_VERB})(?![\\p{L}\\p{N}])`,
  "iu",
);

const REPLY_WORD =
  "repl(?:y|ies|ied|ying)|respon(?:d|ds|ded|ding|se|ses)|answer(?:s|ed|ing)?|get(?:ting)? back|follow[\\s-]?(?:up|ups|ing up)|first (?:message|touch|contact|answer)|nudg(?:e|es|ed|ing)|reach(?:ing)? out";
const NUMBER_WORD = "a|an|one|two|three|four|five|six|seven|eight|nine|ten|fifteen|twenty|thirty|forty|forty[\\s-]?five|sixty|ninety|couple of|few|several|half an?";
const TIME_UNIT = "s|secs?|seconds?|m|mins?|minutes?|h|hrs?|hours?|d|days?|weeks?";
const SPEED_OR_TIME = [
  "fast(?:er|est)?|quick(?:er|est|ly)?|soon(?:er|est)?|prompt(?:ly|er)?|immediate(?:ly)?|instant(?:ly)?|asap|right away|straight away|speed(?:y|ier|ily)?|slow(?:er|ly)?|delay(?:s|ed|ing)?",
  `\\d+(?:[.,]\\d+)?\\s?(?:${TIME_UNIT})`,
  `(?:${NUMBER_WORD})\\s+(?:${TIME_UNIT})`,
  "the hour|same day|overnight|next day",
].join("|");
const REPLY_TIME = new RegExp(
  `(?<![\\p{L}\\p{N}])(?:${REPLY_WORD})(?![\\p{L}\\p{N}])[^.!?\\n]{0,50}(?<![\\p{L}\\p{N}])(?:${SPEED_OR_TIME})(?![\\p{L}\\p{N}])|(?<![\\p{L}\\p{N}])(?:${SPEED_OR_TIME})(?![\\p{L}\\p{N}])[^.!?\\n]{0,50}(?<![\\p{L}\\p{N}])(?:${REPLY_WORD})(?![\\p{L}\\p{N}])`,
  "iu",
);

const CONTROL = W(
  [
    "caps?|capped|capping",
    "limit(?:s|ed|ing)?",
    "maximum|minimum|max|min",
    "thresholds?",
    "timers?|timeouts?",
    "slas?",
    "deadlines?",
    "trigger(?:s|ed|ing)?",
    "escalat(?:e|es|ed|ing|ion|ions)",
    "guardrails?",
    "complian(?:ce|t)",
    "rules?",
    "polic(?:y|ies)",
    "disclos(?:e|es|ed|ing|ure|ures)",
    "ftc",
    "consent",
    "opt[\\s-]?(?:in|out)",
    "unsubscrib(?:e|es|ed|ing)",
    "quiet hours",
    "messaging window|window",
    "frequency",
    "throttl(?:e|es|ed|ing)",
  ].join("|"),
);

/**
 * fix 2 (re-review B1): the FIGURE family. Any digit, currency sign or percent
 * sign, and a number word beside a money unit, an amount word or a
 * more-or-less word. A figure in an owner's note is a price, a discount or a
 * timer in another spelling, and the one owner-facing catalog entry (photo)
 * never needs a number: a false hold costs a note, a false pool costs trust.
 */
const FIGURE_SYMBOL = /[\p{Nd}$£€¥%]/u;
// W integration 2 (W5 review residual): the teens, the fraction words and the percent words joined the lists.
const NUM_WORD =
  "a|an|one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve|thirteen|fourteen|fifteen|sixteen|seventeen|eighteen|nineteen|twenty|thirty|forty|fifty|sixty|seventy|eighty|ninety|couple|couple of|few|several|half|a third|third|a quarter|quarter|a fifth|fifth|a tenth|tenth|hundred|thousand|thou";
const FIGURE_UNIT = "k|grand|hundred|thousand|thou|bucks?|dollars?|pounds?|euros?|quid|percent|per cent|pct|under|over|off|below|above";
const FIGURE_WORDS = new RegExp(
  `(?<![\\p{L}\\p{N}])(?:(?:${NUM_WORD})\\s+(?:${FIGURE_UNIT})|(?:under|over|off|below|above)\\s+(?:${NUM_WORD})|hundreds?|thousands?|grand|percent|per cent|pct)(?![\\p{L}\\p{N}])`,
  "iu",
);

/** fix 2 (probe misses): a send that ignores the 24 hour window, near a send word. */
const WINDOW_PHRASE = "anyway|after a day|next day|after 24|days? later|after a (?:few )?days?";
const SEND_WORD = "send(?:s|ing)?|sent|messag(?:e|es|ing)|text(?:s|ing)?|dm(?:s|ing)?|writ(?:e|ing)|ping(?:s|ing)?";
const WINDOW_SEND = new RegExp(
  `(?<![\\p{L}\\p{N}])(?:${SEND_WORD})(?![\\p{L}\\p{N}])[^.!?\\n]{0,40}(?<![\\p{L}\\p{N}])(?:${WINDOW_PHRASE})(?![\\p{L}\\p{N}])|(?<![\\p{L}\\p{N}])(?:${WINDOW_PHRASE})(?![\\p{L}\\p{N}])[^.!?\\n]{0,40}(?<![\\p{L}\\p{N}])(?:${SEND_WORD})(?![\\p{L}\\p{N}])`,
  "iu",
);

const URGENCY = W(
  "push(?:es|ed|ing)? harder|pressur(?:e|es|ed|ing)|urgency|stall(?:s|ed|ing)?|last chance|today only",
);

/** The cue table, in the order a screen lists what fired. The words printed are these names, never a slice of the note. */
export const IDEA_SCREEN_CUES: ReadonlyArray<{ readonly cue: string; readonly fires: (folded: string) => boolean }> = Object.freeze([
  { cue: "concession", fires: (t: string) => CONCESSION.test(t) },
  { cue: "segment", fires: (t: string) => SEGMENT.test(t) },
  { cue: "lab-grown", fires: (t: string) => LAB_GROWN.test(t) },
  { cue: "disclosure-skip", fires: (t: string) => HIDE_NEAR_DISCLOSURE.test(t) },
  { cue: "reply-time", fires: (t: string) => REPLY_TIME.test(t) },
  { cue: "window-send", fires: (t: string) => WINDOW_SEND.test(t) },
  { cue: "urgency", fires: (t: string) => URGENCY.test(t) },
  { cue: "figure", fires: (t: string) => FIGURE_SYMBOL.test(t) || FIGURE_WORDS.test(t) },
  { cue: "cap-timer-trigger-compliance", fires: (t: string) => CONTROL.test(t) },
]);

export interface IdeaScreenReading {
  readonly held: boolean;
  /** The fixed names of the cues that fired, in table order. */
  readonly cues: readonly string[];
}

/** Reads a note after NFKC folding, so fullwidth letters do not hide a cue. */
export function screenIdeaNote(text: string): IdeaScreenReading {
  const folded = text.normalize("NFKC");
  const cues = IDEA_SCREEN_CUES.filter((c) => c.fires(folded)).map((c) => c.cue);
  return { held: cues.length > 0, cues };
}
